import React, { FC, useEffect, useState } from "react";
import { useForm } from "react-hook-form/dist/index.ie11";
import { useTranslation } from "react-i18next";

import DropZone from "~/components/DropZone";
import TextArea from "~/shared/ui/Fields/TextArea";
import { NOTIFICATIONS } from "~/constans/notifications";
import { useSetLocationData } from "~/hooks/locationData";
import { uploadFile } from "~/libs/uploadFile";
import {
  useAddNotificationMutation,
  useUploadDealersMutation,
  useUploadSrrTargetMutation,
} from "~/schemas/generated/graphql";
import type { UploadDataFormProps } from "~/widgets/UploadDataModal";

import s from "./UploadFileForm.less";

interface UploadFileFormProps extends UploadDataFormProps {
  useUploadMutation:
    | typeof useUploadDealersMutation
    | typeof useUploadSrrTargetMutation;
}

const UploadFileForm: FC<UploadFileFormProps> = ({
  useUploadMutation,
  formId,
  onClose,
  refetch,
  onStateChange,
}) => {
  const { t } = useTranslation();
  const setData = useSetLocationData();
  const [file, setFile] = useState({});
  const [isDisabled, setIsDisabled] = useState(false);
  const { handleSubmit, control } = useForm();

  const [uploadMutation, uploadResult] = useUploadMutation({
    onCompleted() {
      refetch?.();
      onClose();
    },
  });

  const [addNotificationMutation] = useAddNotificationMutation({});
  const fallBack = () => {
    addNotificationMutation({
      variables: {
        type: NOTIFICATIONS.error,
        content: t("common.data_uploaded_error"),
      },
    });
  };

  useEffect(() => {
    onStateChange({
      loading: uploadResult.loading,
      submitDisabled: Object.keys(file).length === 0 || isDisabled,
    });
  }, [uploadResult.loading, file, isDisabled, onStateChange]);

  const onSubmit = async (data) => {
    const { id: fileId } = (await uploadFile(file as File, fallBack)) || {};

    if (fileId) {
      uploadMutation({
        variables: {
          fileId,
          description: data.description,
        },
      });

      setData("page", 0);
      setIsDisabled(true);
    }
  };

  if (uploadResult.error) {
    refetch?.();
    onClose();
  }

  return (
    <form id={formId} className={s.form} onSubmit={handleSubmit(onSubmit)}>
      <TextArea
        name="description"
        label={t("uploads.upload_description")}
        control={control}
        vertical
      />
      <DropZone
        getFiles={(files) => setFile(files[0])}
        accept={".csv"}
        isOne
        showFormats
      />
    </form>
  );
};

export default UploadFileForm;
